import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UserPlus, Check, X, Link2, Trash2, Send, Share2, Zap, Clock, Search } from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';

const SHARE_STATUS_COLORS = { offered: '#F59E0B', accepted: '#22C55E', declined: '#A0A0A0' };
const VERDICT_COLORS = { Pass: '#22C55E', Conditional: '#F59E0B', Fail: '#EF4444' };

export default function Connections() {
  const { user } = useAuth();
  const [connections, setConnections] = useState([]);
  const [shares, setShares] = useState([]);
  const [frays, setFrays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showShare, setShowShare] = useState(null);
  const [sharing, setSharing] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => { loadAll(); }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const loadAll = async () => {
    try {
      const [connRes, sharesRes, fraysRes] = await Promise.all([
        API.get('/api/connections'),
        API.get('/api/shares'),
        API.get('/api/frays'),
      ]);
      setConnections(connRes.data.connections || []);
      setShares(sharesRes.data.shares || []);
      setFrays(fraysRes.data.frays?.filter(f => f.status === 'completed' || f.status === 'stopped') || []);
    } catch {}
    setLoading(false);
  };

  const searchUsers = useCallback(async (q) => {
    if (!q || q.length < 1) { setSearchResults([]); setShowDropdown(false); return; }
    setSearching(true);
    try {
      const res = await API.get(`/api/users/search?q=${encodeURIComponent(q)}`);
      setSearchResults(res.data.users || []);
      setShowDropdown(true);
    } catch { setSearchResults([]); }
    setSearching(false);
  }, []);

  const handleSearchInput = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchUsers(val), 300);
  };

  const sendRequestToUser = async (targetEmail) => {
    setSending(true); setError(''); setSuccess('');
    try {
      await API.post('/api/connections/request', { email: targetEmail });
      setSuccess(`Connection request sent to ${targetEmail}`);
      setSearchQuery('');
      setSearchResults([]);
      setShowDropdown(false);
      await loadAll();
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
    setSending(false);
  };

  const acceptConn = async (id) => { try { await API.post(`/api/connections/${id}/accept`); await loadAll(); } catch {} };
  const declineConn = async (id) => { try { await API.post(`/api/connections/${id}/decline`); await loadAll(); } catch {} };
  const removeConn = async (id) => { try { await API.delete(`/api/connections/${id}`); await loadAll(); } catch {} };

  const shareFray = async (frayId, targetUserId) => {
    setSharing(true); setError('');
    try {
      await API.post(`/api/frays/${frayId}/share-with`, { user_id: targetUserId });
      setShowShare(null);
      setSuccess('Fray shared! Waiting for them to accept.');
      setTimeout(() => setSuccess(''), 3000);
      await loadAll();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to share'); }
    setSharing(false);
  };

  const acceptShare = async (id) => { try { await API.post(`/api/shares/${id}/accept`); await loadAll(); } catch {} };
  const declineShare = async (id) => { try { await API.post(`/api/shares/${id}/decline`); await loadAll(); } catch {} };

  const pending = connections.filter(c => c.status === 'pending' && c.direction === 'received');
  const accepted = connections.filter(c => c.status === 'accepted');
  const sent = connections.filter(c => c.status === 'pending' && c.direction === 'sent');

  const incomingShares = shares.filter(s => s.direction === 'received' && s.status === 'offered');
  const sentShares = shares.filter(s => s.direction === 'sent');
  const acceptedShares = shares.filter(s => s.direction === 'received' && s.status === 'accepted');

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="pt-20 pb-12 px-6 max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold font-['Barlow_Condensed'] mb-2" data-testid="connections-title">Connections</h1>
        <p className="text-[#A0A0A0] text-sm mb-6">Connect with Fray users and share debates.</p>

        {error && <div className="p-3 mb-4 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-sm">{error}</div>}
        {success && <div className="p-3 mb-4 bg-[#22C55E]/10 border border-[#22C55E]/30 text-[#22C55E] text-sm">{success}</div>}

        {/* Connect */}
        <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] mb-8" ref={searchRef}>
          <h3 className="text-xs font-mono text-[#A0A0A0] mb-3">CONNECT WITH A USER</h3>
          <div className="relative">
            <div className="flex items-center gap-2 px-4 py-2 bg-[#0F0F0F] border border-[#2A2A2A] focus-within:border-[#FF4D00]">
              <Search className="w-4 h-4 text-[#A0A0A0] shrink-0" />
              <input type="text" value={searchQuery} onChange={handleSearchInput}
                onFocus={() => { if (searchResults.length > 0) setShowDropdown(true); }}
                className="flex-1 bg-transparent text-sm focus:outline-none"
                placeholder="Search by name or email..." data-testid="connect-search" />
              {searching && <div className="w-4 h-4 border-2 border-[#FF4D00] border-t-transparent rounded-full animate-spin" />}
            </div>
            {showDropdown && searchResults.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-[#1A1A1A] border border-[#2A2A2A] max-h-60 overflow-y-auto" data-testid="search-results-dropdown">
                {searchResults.map(u => (
                  <button key={u.id} onClick={() => sendRequestToUser(u.email)} disabled={sending}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#252525] transition-colors text-left border-b border-[#2A2A2A] last:border-b-0 disabled:opacity-50"
                    data-testid={`search-result-${u.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 bg-[#FF4D00]/10 border border-[#FF4D00]/30 flex items-center justify-center text-xs font-bold text-[#FF4D00] shrink-0">
                        {(u.name || u.email || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        {u.name && <span className="text-sm font-medium block truncate">{u.name}</span>}
                        <span className="text-xs text-[#A0A0A0] font-mono block truncate">{u.email}</span>
                      </div>
                    </div>
                    <Send className="w-4 h-4 text-[#FF4D00] shrink-0 ml-2" />
                  </button>
                ))}
              </div>
            )}
            {showDropdown && searchQuery.length >= 1 && searchResults.length === 0 && !searching && (
              <div className="absolute z-50 w-full mt-1 bg-[#1A1A1A] border border-[#2A2A2A] p-4 text-center">
                <p className="text-sm text-[#A0A0A0]">No users found for "{searchQuery}"</p>
              </div>
            )}
          </div>
        </div>

        {/* Incoming connection requests */}
        {pending.length > 0 && (
          <Section title="Connection Requests" badge={pending.length} badgeColor="#FF4D00">
            {pending.map(c => (
              <div key={c.id} className="p-3 bg-[#1A1A1A] border border-[#FF4D00]/30 flex items-center justify-between" data-testid={`pending-${c.id}`}>
                <div>
                  <span className="text-sm font-medium">{c.other_name}</span>
                  <span className="text-xs text-[#A0A0A0] font-mono ml-2">{c.other_email}</span>
                  <span className="text-[9px] text-[#A0A0A0] block mt-0.5">wants to connect with you</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => acceptConn(c.id)} className="px-3 py-1.5 bg-[#22C55E] text-black text-xs font-medium flex items-center gap-1"><Check className="w-3 h-3" /> Accept</button>
                  <button onClick={() => declineConn(c.id)} className="px-3 py-1.5 border border-[#2A2A2A] text-xs text-[#A0A0A0]"><X className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
          </Section>
        )}

        {/* Incoming Fray share offers */}
        {incomingShares.length > 0 && (
          <Section title="Shared Frays — Pending" badge={incomingShares.length} badgeColor="#A855F7">
            {incomingShares.map(s => (
              <div key={s.id} className="p-3 bg-[#1A1A1A] border border-[#A855F7]/30" data-testid={`incoming-share-${s.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Share2 className="w-3.5 h-3.5 text-[#A855F7]" />
                      <span className="text-sm font-medium">{s.shared_by_name}</span>
                      <span className="text-[9px] text-[#A855F7] font-mono">wants to share a Fray</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#A0A0A0]">
                      <span className="font-mono">{s.fray_title}</span>
                      <span>—</span>
                      <span className="truncate">{s.fray_scenario}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0 ml-3">
                    <button onClick={() => acceptShare(s.id)} className="px-3 py-1.5 bg-[#22C55E] text-black text-xs font-medium flex items-center gap-1"><Check className="w-3 h-3" /> Accept</button>
                    <button onClick={() => declineShare(s.id)} className="px-3 py-1.5 border border-[#2A2A2A] text-xs text-[#A0A0A0]">Decline</button>
                  </div>
                </div>
              </div>
            ))}
          </Section>
        )}

        {/* Connected users */}
        <Section title={`Connected (${accepted.length})`}>
          {accepted.length === 0 ? (
            <div className="p-6 bg-[#1A1A1A] border border-[#2A2A2A] text-center">
              <Link2 className="w-8 h-8 text-[#2A2A2A] mx-auto mb-3" />
              <p className="text-[#A0A0A0] text-sm">No connections yet.</p>
            </div>
          ) : (
            accepted.map(c => (
              <div key={c.id} className="bg-[#1A1A1A] border border-[#2A2A2A]" data-testid={`connected-${c.id}`}>
                <div className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#22C55E]/10 border border-[#22C55E]/30 flex items-center justify-center text-xs font-bold text-[#22C55E]">
                      {(c.other_name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <span className="text-sm font-medium">{c.other_name}</span>
                      <span className="text-xs text-[#A0A0A0] font-mono ml-2">{c.other_email}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setShowShare(showShare === c.id ? null : c.id)}
                      className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 border transition-colors ${showShare === c.id ? 'border-[#FF4D00] text-[#FF4D00]' : 'border-[#2A2A2A] text-[#A0A0A0] hover:text-[#FF4D00]'}`}>
                      <Share2 className="w-3 h-3" /> Share Fray
                    </button>
                    <button onClick={() => removeConn(c.id)} className="p-1.5 text-[#A0A0A0] hover:text-[#EF4444]">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {showShare === c.id && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="border-t border-[#2A2A2A] p-3">
                    <p className="text-xs text-[#A0A0A0] mb-2">Select a Fray to share with {c.other_name}:</p>
                    {frays.length === 0 ? (
                      <p className="text-xs text-[#A0A0A0]">No Frays available to share.</p>
                    ) : (
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {frays.map(f => {
                          const alreadyShared = sentShares.some(s => s.fray_id === f.id && s.shared_with_name === c.other_name);
                          return (
                            <button key={f.id} onClick={() => !alreadyShared && shareFray(f.id, c.other_user_id)} disabled={sharing || alreadyShared}
                              className={`w-full flex items-center justify-between px-3 py-2 bg-[#0F0F0F] border text-left transition-colors disabled:opacity-40 ${alreadyShared ? 'border-[#22C55E]/20' : 'border-[#2A2A2A] hover:border-[#FF4D00]/40'}`}>
                              <div className="min-w-0 flex-1">
                                <span className="text-xs font-mono text-[#A0A0A0]">{f.template_name}</span>
                                <p className="text-xs text-[#F5F5F5] truncate">{f.scenario?.slice(0, 80)}</p>
                              </div>
                              <div className="flex items-center gap-2 ml-2 shrink-0">
                                {f.verdict?.verdict && (
                                  <span className="text-[9px] font-mono px-1.5 py-0.5 border"
                                    style={{ color: VERDICT_COLORS[f.verdict.verdict], borderColor: VERDICT_COLORS[f.verdict.verdict] + '40' }}>
                                    {f.verdict.verdict}
                                  </span>
                                )}
                                {alreadyShared && <span className="text-[8px] font-mono text-[#22C55E]">SENT</span>}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            ))
          )}
        </Section>

        {/* Sent share offers */}
        {sentShares.length > 0 && (
          <Section title="Shares You Sent">
            {sentShares.map(s => (
              <div key={s.id} className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-[#A0A0A0]">{s.fray_title}</span>
                    <span className="text-[9px] px-1.5 py-0.5 border font-mono capitalize"
                      style={{ color: SHARE_STATUS_COLORS[s.status], borderColor: SHARE_STATUS_COLORS[s.status] + '40' }}>
                      {s.status}
                    </span>
                  </div>
                  <p className="text-xs text-[#A0A0A0] truncate mt-0.5">{s.fray_scenario}</p>
                </div>
                <span className="text-xs text-[#A0A0A0] ml-2 shrink-0">to {s.other_name}</span>
              </div>
            ))}
          </Section>
        )}

        {/* Accepted shared Frays */}
        {acceptedShares.length > 0 && (
          <Section title="Frays Shared With You">
            {acceptedShares.map(s => (
              <Link key={s.id} to={`/fray/${s.fray_id}/results`}
                className="block p-3 bg-[#1A1A1A] border border-[#22C55E]/20 hover:border-[#22C55E]/40 transition-colors">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono text-[#A0A0A0]">{s.fray_title}</span>
                  <span className="text-[9px] text-[#A855F7] font-mono">from {s.shared_by_name}</span>
                </div>
                <p className="text-xs text-[#F5F5F5] truncate">{s.fray_scenario}</p>
              </Link>
            ))}
          </Section>
        )}

        {/* Sent connection requests */}
        {sent.length > 0 && (
          <Section title="Sent Requests">
            {sent.map(c => (
              <div key={c.id} className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-between">
                <div>
                  <span className="text-sm">{c.other_name || c.other_email}</span>
                  <span className="text-[9px] font-mono text-[#F59E0B] ml-2">PENDING</span>
                </div>
                <button onClick={() => removeConn(c.id)} className="text-xs text-[#A0A0A0] hover:text-[#EF4444]">Cancel</button>
              </div>
            ))}
          </Section>
        )}
      </div>
    </div>
  );
}

function Section({ title, badge, badgeColor, children }) {
  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold font-['Barlow_Condensed'] mb-3 flex items-center gap-2">
        {title}
        {badge > 0 && (
          <span className="text-xs font-mono px-2 py-0.5" style={{ backgroundColor: (badgeColor || '#FF4D00') + '15', color: badgeColor || '#FF4D00' }}>
            {badge}
          </span>
        )}
      </h2>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
